import type { ReceiptOrder, ReceiptOrderItem, ReceiptOrderDynamodb, ReceiptItemCustomSection } from "../interface/ReceiptOrder";
import { usePrintCustomSection } from "~/composables/usePrintCustomSection";
import { joinToppingLabels } from "~/utils/itemCustomLines";
import type { Table } from "../interface/Table";
import type { TableItem } from "../interface/TableItem";
import type { TableBilling } from "../interface/TableBilling";
import type { ReceiptPayment } from "~/interface/ReceiptPayment";
import dayjs from "dayjs";
import { timestamp } from "@vueuse/core";
import { usePosProductsStore } from "~/stores/pos.products";
import { useSettingsStore } from "~/stores/settings";
import { roundMoney, checkBillInvariant } from "~/utils/money";

// Shops whose receipts feed an external PMS that still expects the legacy
// field set. See legacyPmsFields() below.
// pen's bangkalore Resort,  Tropica  
const LEGACY_PMS_CUSTOMER_IDS = [5224, 6920];  
 

// Per-line item discount on the receipt. Controlled by the system env
// "ShowItemDiscount" (Settings → ข้อมูลร้าน). Missing/blank => OFF; only the
// string 'true' (or '1') turns it on.
function isShowDiscountItem(): boolean {
  try {
    return useSettingsStore().showItemDiscount === true;
  } catch {
    return false;
  }
}


/**
 * The only tag a stored or printed receipt is read for is `skip`: the print
 * service hides a line tagged "skip" whose ExtendedPrice is 0 (PrintServiceV2
 * Graphic/ReceiptV5.cs, PreviewV5.cs and the legacy ReceiptV2.cs all test that
 * exact string).
 *
 * `printer1`..`printer4` route KITCHEN slips, and those are built from a
 * separate job payload (OrderItemHelper) that never reads a receipt.
 * `no-discount` is read once, in fromTable, off the in-memory object — before
 * this runs.
 *
 * Returns undefined when nothing survives so the caller omits the key. Never
 * emit null: the print service's ReceiptItem defaults `Tags` to an empty list,
 * but only while the key is ABSENT — an explicit null overwrites that and
 * `Tags.Contains("skip")` throws.
 */
function receiptTags(tags: unknown): string[] | undefined {
  if (!Array.isArray(tags)) return undefined;
  const kept = tags.filter((t) => String(t).trim().toLowerCase() === "skip");
  return kept.length ? kept : undefined;
}

function buildItemDiscountCustom(
  discountAmount: number,
  discountLabel: string
): ReceiptItemCustomSection | undefined {
  if (!isShowDiscountItem()) return undefined;
  if (!(discountAmount > 0)) return undefined;

  const { buildDiscountSection } = usePrintCustomSection();
  return buildDiscountSection(discountAmount, discountLabel, {Italic:true});
}

export class ReceiptOrderClass implements ReceiptOrder {
  OrderItems: Array<ReceiptOrderItem> = [];
  OrderId: number = 0;
  Date: string = "";
  CustomerId: number = 0;
  Received: number = 0;
  Change: number = 0;
  SubTotal: number = 0;
  Rounding: number = 0;

  ItemDiscount: number = 0;

  ServiceCharge: number = 0;
  ServiceChargeText: string = "";

  Discount: number = 0;
  DiscountText: string = "";
  DiscountRequest: { mode: number; value: number } = { mode: 0, value: 0 };

  CardCharge: number = 0;
  CardChargeText: string = "";

  Vat: number = 0;
  VatText: string = "";
  VatValue: number = 0;
  VatMode: string = "";

  PaymentType: string = "";
  PaymentMethod: number = 0;

  GrandTotal: number = 0;
  CustomerCount: number = 0;
  Table: string = "";
  IsDeleted: boolean = false;
  IsOnTheHouse: boolean = false;
  ShiftWork: string = "";
  User: string = "";
  Note: string | null = null;

  Synced?: boolean = false;
  Nonce?: string = Math.random().toString(36).substring(2, 15);

  // Receipt SubTotal convention (read by the server, see
  // niceloop-pos-server-v1/src/utils/subtotalConvention.ts):
  //   'B' -> SubTotal is GROSS (before item discount). This is what we emit.
  //   '2026-02-05' -> legacy NET SubTotal. Never emit this.
  version: string = "B";

  // Additional properties for ReceiptOrderDynamodb or internal use
  VarMode: string = "";
  BeforeRoundingV2: number = 0;
  RoundingV2: number = 0;
  RoundingV2To: number | null = null;
  BeforeVatV2: number = 0;
  Ref1: string = "";
  Key: string = "";
  DateString: string = "";
  CustomHeaders: Array<{ name: string; value: string | boolean }> = [];
  CustomFooters: Array<{ name: string; value: string }> = [];
  TempId: number = 0;
  UniqueId: string = "";
  Uuid: string = "";
  Reference: string = "";
  FullAlias: string = "";
  ReceiptId: string = "";
  Cashier: string = "";
  Room: string | null = null;
  timestamp: string = "";
  multiplePayment?: any;
  BusinessDate: string = "";

  payment: ReceiptPayment = {} as ReceiptPayment;

  constructor(data?: Partial<ReceiptOrder>) {
    if (data) {
      Object.assign(this, data);
    }
  }

  static fromTable(table: Table, options: any = {}): ReceiptOrderClass {
    const receiptOrder = new ReceiptOrderClass();

    receiptOrder.Table = table.nickName || table.name || "";
    receiptOrder.CustomerCount = table.guests || 0;
    receiptOrder.Date = dayjs(table.endDateTime).format('YYYY-MM-DDTHH:mm:ssZ');
    receiptOrder.OrderId = options.orderId || 0;
    receiptOrder.CustomerId = options.customerId || 0;



    if (table.items && Array.isArray(table.items)) {
      // Look up item tags (e.g. "skip") by sub-item id so the print service can
      // hide buffet lines (tag "skip" + extendedPrice 0). TableItem carries no
      // tags itself; the products store holds the catalog tags.
      const productsStore = usePosProductsStore();
      receiptOrder.OrderItems = table.items.map((item: TableItem, index: number) => {
        const receiptItem = ReceiptOrderClass.convertTableItemToReceiptOrderItem(item, index + 1, options.orderId, options.customerId)
        // The full catalog tags stay on the object for now — the mode-2
        // no-discount marker below reads them. `toDynamodb()` narrows them to
        // the only one a receipt is ever read for.
        const tags = productsStore.getPrintersByUuid(item.subItemId) || []
        if (tags.length) receiptItem.tags = tags
        return receiptItem
      });
    }

    if (table.billing) {
      receiptOrder.applyBilling(table.billing);

    }

    // Legacy "mode 2" marker: when the whole-bill discount excludes no-discount
    // lines, append ` *` to the name of each line that DOES receive the discount
    // (i.e. not no-discount-tagged and not already line-discounted). Lines that
    // are excluded print unchanged. Only in mode 2 with a positive value.
    const dc2 = table.billing?.dc2
    if (Array.isArray(dc2) && dc2[0] === 2 && (dc2[1] || 0) > 0) {
      receiptOrder.OrderItems.forEach((it: any) => {
        const isExcluded = (it.tags || []).some((t: any) => String(t).trim().toLowerCase() === 'no-discount')
          || (it.discountRequest?.value || 0) > 0
        if (!isExcluded) {
          it.name = `${it.name || ''} *`
        }
      })
    }

    receiptOrder.applyPayment(options.payment)
    receiptOrder.applyShiftWork(options.shiftWork)
    receiptOrder.applyUser(options.user)
    // The receipt summary blocks used to be built here and persisted. They are
    // pure presentation and their shape depends on the printer interface, so
    // they are derived at print time instead — see
    // app/class/printer/payload/derive/summarySections.ts.

    return receiptOrder;
  }

  // `row`, `orderId` and `customerId` used to be copied onto every line. Nothing
  // read them back — the row index is the array index, and both ids are already
  // on the receipt — so they are no longer stored. The parameters stay so the
  // existing call sites and tests keep working.
  static convertTableItemToReceiptOrderItem(
    tableItem: TableItem,
    _row?: number,
    _orderId?: number,
    _customerId?: number
  ): ReceiptOrderItem {

    const extendedPrice = tableItem.extendedPrice || ((tableItem.quantity || 0) * (tableItem.price || 0));
    const discount = extendedPrice - (tableItem.discountedPrice);


    let discountText = "";
    if (tableItem.dc1[0] == 0 && tableItem.dc1[1] > 0) {
      discountText = `ลด ${tableItem.dc1[1]}%`
    }

    if (tableItem.dc1[0] == 1 && tableItem.dc1[1] > 0) {
      // Baht discounts are per piece, so the slip spells out the multiplication
      // in the legacy Food.Web wording a migrated shop already reads: `ลด -30.00 X 5`.
      discountText = `ลด -${Number(tableItem.dc1[1]).toFixed(2)} X ${tableItem.quantity || 0}`
    }



    const toppings = tableItem.toppings || [];
    // Stays ONE comma-joined string on the customer receipt (unlike the kitchen
    // slip, which splits it per line) — but a topping ordered twice now reads
    // `ไข่ดาว x2` instead of losing the count. Same formatter as the kitchen slip.
    const toppingList = joinToppingLabels(toppings);
    const comments = tableItem.comments || "";
    const dc1Mode = tableItem.dc1 ? tableItem.dc1[0] : 0;
    const dc1Value = tableItem.dc1 ? tableItem.dc1[1] : 0;

    return {
      quantity: tableItem.quantity || 0,
      price: tableItem.price || 0,
      extendedPrice: extendedPrice,
      discount: discount,
      discountedPrice: tableItem.discountedPrice,

      subItemId: typeof tableItem.subItemId === 'string' ? parseInt(tableItem.subItemId) || 0 : 0,
      name: tableItem.name || "",
      // mode 1 → `value` is the PER-PIECE discount; the line's total is in
      // `discount` / `discountedPrice` above.

      // Emitted only when they say something. A plain line has no comment, no
      // toppings and no discount, and storing "", [] and false for all of that
      // was most of the row. Every reader treats absent the same as empty —
      // the printer guards the strings with IsNullOrEmpty and reads
      // DiscountText only when Discount > 0, and the server's write-side
      // allowlist drops these too (receiptImageSchema.ts).
      // `isDeleted` is likewise omitted: it was always written false here, and
      // readers test `!item.isDeleted`.
      ...(toppings.length ? { toppings, toppingList } : {}),
      ...(comments ? { comments } : {}),
      ...(discountText ? { discountText } : {}),
      // `{ mode: 0, value: 0 }` is the "this line was not discounted" default —
      // it says nothing the line's own `discount: 0` does not. The printer's
      // ReceiptItem has no per-line DiscountRequest at all (only the BILL-level
      // one is rendered) and the server reads neither.
      ...(dc1Value > 0 ? { discountRequest: { mode: dc1Mode, value: dc1Value } } : {}),
      // Extra printed row for the line discount (shows on print / reprint /
      // preview). Only present when the line is discounted and the feature is on;
      // otherwise the field is omitted entirely.
      ...(() => {
        const custom = buildItemDiscountCustom(discount, discountText);
        return custom ? { Custom: custom } : {};
      })(),
      // Carry the service/VAT exemption onto the stored receipt, matching the
      // legacy payload's `skipService`. Only emitted when true (same reasoning as
      // openMenuName in calculateItems): strategies that ignore the tag never set
      // it, so no other shop's receipt payload changes shape.
      ...(tableItem.skipService ? { skipService: true } : {})
    };
  }

  applyBilling(billing: TableBilling): void {

    // SubTotal คำนวนจาก  extendedPrice — GROSS (ก่อนหักส่วนลดรายบรรทัด) = version 'B'
    this.SubTotal = roundMoney(this.OrderItems.reduce((sum, item) => sum + (item.extendedPrice || 0), 0));
    this.GrandTotal = roundMoney(billing.grandTotal || 0);

    this.ItemDiscount = roundMoney(billing.dc1 || 0);

    if (billing.dc2 && Array.isArray(billing.dc2) && billing.dc2.length >= 2) {
      this.Discount = roundMoney(billing.dc2[2] || 0);
      this.DiscountRequest = {
        mode: billing.dc2[0],
        value: billing.dc2[1]
      };
      if (billing.dc2[1] > 0) {
        // mode 2 = percent excluding no-discount-tagged / already-discounted lines
        // (legacy Food "*X% (exclude discounted items)").
        this.DiscountText = billing.dc2[0] === 2
          ? `*-${billing.dc2[1]}%*`
          : `Discount ${billing.dc2[1]}%`;
      }
    }

    // sc / cc tuples are [mode, value, amount]. Mode 0 = percent — the label
    // carries the rate (e.g. "10%"). Any other mode (1 = fixed baht) has NO
    // rate label: `value` then holds a baht amount, so "${value}%" would print
    // a fixed 100฿ charge as "100%". The amount prints in the value column
    // instead, leaving the label as a bare "Service Charge" / "Card Charge".
    // Mirrors useBillingSummaryParts' percentModes = [0]. The mode is lost at
    // print time (toImage keeps only the text), so this must be decided here.
    if (billing.sc && Array.isArray(billing.sc) && billing.sc.length >= 2) {
      this.ServiceCharge = roundMoney(billing.sc[2] || 0);
      this.ServiceChargeText =
        Number(billing.sc[0]) === 0 && billing.sc[1] > 0 ? `${billing.sc[1]}%` : "";
    }

    if (billing.cc && Array.isArray(billing.cc) && billing.cc.length >= 2) {
      this.CardCharge = roundMoney(billing.cc[2] || 0);
      this.CardChargeText =
        Number(billing.cc[0]) === 0 && billing.cc[1] > 0 ? `${billing.cc[1]}%` : "";
    }

    if (billing.vat && Array.isArray(billing.vat) && billing.vat.length >= 2) {
      this.Vat = roundMoney(billing.vat[2] || 0);
      if (billing.vat[0] == 1) {
        this.VatMode = "1";
        this.VatValue = billing.vat[1];
        this.VatText = `${billing.vat[1]}% (Included)`;
        this.VarMode = "1";
      } else if (billing.vat[0] == 2) {
        this.VatMode = "2";
        this.VatValue = billing.vat[1];
        this.VatText = `${billing.vat[1]}% (Excluded)`;
        this.VarMode = "2";
      } else {
        this.VatText = "";
        this.VatMode = "";
        this.VatValue = 0;
      }
    }

    this.ItemDiscount = roundMoney(this.OrderItems.reduce((sum, item) => sum + (item.discount || 0), 0));

    // `Rounding` is the residual plug, always DERIVED from the fields we actually
    // store — never copied from billing.rounding. Deriving it here makes the
    // invariant
    //   SubTotal - ItemDiscount + ServiceCharge - Discount + CardCharge
    //     + VAT(exclusive only) + Rounding === GrandTotal
    // hold by construction, so the printed slip and the server report agree to
    // the satang and the server's read-time repair becomes a no-op.
    //
    // Inclusive VAT (mode 1) is already inside SubTotal — adding it here would
    // force Rounding to absorb ~-VAT (thousands of baht). Only exclusive VAT
    // (mode 2) is part of the total.
    this.BeforeVatV2 = roundMoney(
      this.SubTotal - this.ItemDiscount - this.Discount + this.ServiceCharge + this.CardCharge
    );
    const vatInTotal = this.VatMode == "2" ? this.Vat : 0;
    this.BeforeRoundingV2 = roundMoney(this.BeforeVatV2 + vatInTotal);
    this.Rounding = roundMoney(this.GrandTotal - this.BeforeRoundingV2);
    this.RoundingV2 = this.Rounding;
    // RoundingV2To is left as null or default since it's not explicitly in TableBilling

    // Guard rail — log only. A malformed bill must never block a real payment,
    // but we want it loud in dev/QA (see money.ts / HANDOFF-money-rounding-frontend.md).
    const invariantFields = {
      SubTotal: this.SubTotal,
      ItemDiscount: this.ItemDiscount,
      Discount: this.Discount,
      ServiceCharge: this.ServiceCharge,
      CardCharge: this.CardCharge,
      Vat: this.Vat,
      VatMode: this.VatMode,
      Rounding: this.Rounding,
      GrandTotal: this.GrandTotal,
    };
    const invariantError = checkBillInvariant(invariantFields);
    if (invariantError) {
      console.error(`[ReceiptOrder] bill invariant violated: ${invariantError}`, invariantFields);
    }
  }

  applyPayment(payload: ReceiptPayment): ReceiptOrderClass {

    this.payment = payload;

    this.Received = roundMoney(payload.list.reduce((sum, item) => sum + (item.amountInput || 0), 0));
    this.Change = roundMoney(this.Received - payload.list.reduce((sum, item) => sum + (item.amount || 0), 0));
    this.PaymentType = payload.list.length == 1 ? (payload.list[0]?.name || "") : this.__convertMultiplePaymentTypeToString(payload, true);
    this.IsOnTheHouse = payload.list.length == 1 ? (payload.list[0]?.isEntertain ?? false) : false;
    this.Note = payload.note?.trim() || null
    const room = payload.room?.trim()
    this.Room = room ? room.toUpperCase() : null

    this.multiplePayment = payload.list.length == 1 ? (payload.list[0]?.name || "") : this.__convertMultiplePaymentTypeToString(payload, false);
    return this;
  }

  __convertMultiplePaymentTypeToString(payload: ReceiptPayment, forceToString: boolean = true): string | any {

    //exmaple repsonse : "[{\"name\":\"Cash\",\"amount\":100,\"change\":0,\"receive\":100},{\"name\":\"MASTER CARD\",\"amount\":250,\"receive\":250}]"



    const result = payload.list.map((item) => ({
      name: item.name || "",
      amount: item.amount || 0,
      change: (item.amountInput || 0) - (item.amount || 0),
      receive: item.amountInput || 0,
    }));

    return forceToString ? JSON.stringify(result) : result;
  }

  applyShiftWork(shiftWork: string): ReceiptOrderClass {
    this.ShiftWork = shiftWork;
    return this;
  }

  applyUser(user: string): ReceiptOrderClass {
    this.User = user;
    return this;
  }

  toJSON(): ReceiptOrder {

    //if CustomerId is 0, throw error
    if (this.CustomerId == 0) {
      throw new Error("CustomerId is required");
    }


    return {
      OrderItems: this.OrderItems,
      OrderId: this.OrderId,
      Date: this.Date,
      CustomerId: parseInt(this.CustomerId as any),
      Received: this.Received,
      Change: this.Change,
      SubTotal: this.SubTotal,
      Rounding: this.Rounding,

      ItemDiscount: this.ItemDiscount,

      ServiceCharge: this.ServiceCharge,
      ServiceChargeText: this.ServiceChargeText,

      Discount: this.Discount,
      // `DiscountRequest` is the source of truth; `DiscountText` is a label
      // derived from it at print time.
      DiscountRequest: this.DiscountRequest,

      CardCharge: this.CardCharge,
      // Rate texts are NOT derivable — the percentage the cashier applied is
      // recorded nowhere else. `VatText` is, from VatMode + VatValue.
      CardChargeText: this.CardChargeText,

      Vat: this.Vat,
      VatValue: this.VatValue,
      VatMode: this.VatMode,

      PaymentType: this.PaymentType,

      GrandTotal: this.GrandTotal,
      CustomerCount: this.CustomerCount,
      Table: this.Table,
      IsDeleted: false,
      IsOnTheHouse: this.IsOnTheHouse,
      ShiftWork: this.ShiftWork,
      User: this.User,
      Note: this.Note,

      Synced: this.Synced,
      Nonce: this.Nonce,

      // 'B' = SubTotal is GROSS and every money field is already 2 decimals.
      // Never emit '2026-02-05' (legacy NET-SubTotal) — the server would add the
      // item discount back and double-count it.
      version: this.version,

    };
  }

  // Fields the legacy POS wrote on every receipt and that the external PMS of
  // one shop still depends on: it de-duplicates incoming orders by ReceiptId
  // (numeric, mirroring OrderId) and reads Room even when unset — an absent
  // ReceiptId made every bill look like the same already-seen order and the
  // integration skipped all of them. Scoped to that shop so every other
  // customer's payload stays byte-identical to before.
  private legacyPmsFields(): Partial<ReceiptOrderDynamodb> {
    const customerId = Number(this.CustomerId);
    if (!LEGACY_PMS_CUSTOMER_IDS.includes(customerId)) return {};
    return {
      ShopId: customerId,
      ReceiptId: this.OrderId,
      Room: this.Room ?? null,
    };
  }

  /**
   * The stored shape. Everything omitted here is derived on the way out —
   * either by the server's read path or by the print-payload strategy in
   * `app/class/printer/payload` — so nothing is lost by not writing it:
   *
   *   Summary1..4, CustomHeaders, CustomFooters  print presentation, rebuilt per job
   *   BeforeVatV2, BeforeRoundingV2, RoundingV2  GrandTotal - Rounding
   *   RoundingV2To, VarMode                      always null / a misspelt VatMode
   *   Ref1, Key, UniqueId, Uuid, TempId          copies of Nonce, reminted per job
   *   DateString, VatText, DiscountText          Date / VatMode+VatValue / DiscountRequest
   *   FullAlias                                  a second copy of Reference
   *   Cashier, timestamp, PaymentMethod          always empty or a constant 0
   *
   * `Synced` and `Nonce` stay: they are how the offline queue de-duplicates and
   * decides pending-vs-synced. `Synced` is stripped at the POST boundary rather
   * than here, so the local mirror keeps it.
   */
  toDynamodb(): ReceiptOrderDynamodb {
    return {
      ...this.toJSON(),
      // Narrowed here rather than at build time: fromTable's mode-2 marker
      // reads the full catalog tags off the in-memory object first.
      OrderItems: this.OrderItems.map((item) => {
        const tags = receiptTags((item as any).tags);
        if (tags) return { ...item, tags };
        const { tags: _dropped, ...rest } = item as any;
        return rest as ReceiptOrderItem;
      }),
      DiscountRequest: this.DiscountRequest,
      ServiceChargeText: this.ServiceChargeText,
      CardChargeText: this.CardChargeText,
      VatValue: this.VatValue,
      VatMode: this.VatMode,
      Note: this.Note,
      BusinessDate: this.BusinessDate || usePosJobsStore().getBusinessDateFromTimestamp(this.Date),
      Reference: this.Reference,
      ReceiptId: this.ReceiptId,
      // Skip Room unless it actually has a value — don't bloat every receipt
      // with "Room": null for the ~99% of shops that don't use Room Service.
      ...(this.Room ? { Room: this.Room } : {}),
      ...this.legacyPmsFields(),
      multiplePayment: this.multiplePayment || null,
    };
  }
}

export default ReceiptOrderClass;
